import { secureRandom } from '@/lib/secureRandom';
import { create } from 'zustand';
import { apiClient } from '../lib/api';

/**
 * Attendance (People service, /people/attendance). snake_case → camelCase is
 * done here, never in components. Actions throw an Error carrying the server's
 * message and machine code (e.g. ALREADY_CHECKED_IN) so screens can explain
 * exactly what happened.
 */

// ───────────────────────────── types ─────────────────────────────

export type SessionStatus = 'OPEN' | 'COMPLETED' | 'MISSING_CHECKOUT' | 'SUPERSEDED' | 'ADJUSTED' | 'CANCELLED';
export type LocationStatus = 'VALID' | 'OUTSIDE_ALLOWED_LOCATION' | 'UNASSIGNED_LOCATION' | 'LOW_ACCURACY' | 'UNAVAILABLE' | 'NOT_CHECKED';
export type DayStatus = 'PRESENT' | 'HALF_DAY' | 'ABSENT' | 'IN_PROGRESS' | 'MISSING_CHECKOUT' | 'ON_LEAVE' | 'HOLIDAY' | 'WEEK_OFF' | 'NOT_CHECKED_IN';
export type AdjustmentType = 'MISSED_CHECK_IN' | 'MISSED_CHECK_OUT' | 'MISSED_BOTH' | 'TIME_CORRECTION' | 'LOCATION_CORRECTION'
    | 'WORK_DURATION_CORRECTION' | 'SESSION_CORRECTION' | 'OTHER';

export interface AttendanceSession {
    id: string;
    date: string;
    number: number;
    /** Revision of the numbered session: 0 = original, 1+ = an approved adjustment of it. */
    revision: number;
    /** Display label for the session: "1" for an original, "1(a)", "1(b)" … for its revisions. */
    label: string;
    status: SessionStatus;
    checkIn: string;
    checkOut: string | null;
    checkInLocation: string | null;
    checkOutLocation: string | null;
    checkInLocationStatus: LocationStatus | null;
    checkOutLocationStatus: LocationStatus | null;
    checkInDistance: number | null;
    checkInAccuracy: number | null;
    workMode: string | null;
    creditedMinutes: number | null;
    needsReview: boolean;
    exception: string | null;
    adjustmentId: string | null;
}

export interface DaySummary {
    date: string;
    status: DayStatus;
    dayType: 'WORKING' | 'HOLIDAY' | 'WEEK_OFF' | 'LEAVE';
    workMode: string | null;
    firstCheckIn: string | null;
    lastCheckOut: string | null;
    workedMinutes: number;
    expectedMinutes: number | null;
    sessions: number;
    isLate: boolean;
    lateMinutes: number;
    isEarlyCheckout: boolean;
    earlyMinutes: number;
    hasMissingCheckout: boolean;
    hasLocationException: boolean;
    adjustmentPending: boolean;
    adjustmentCount: number;
}

export interface AttentionItem { sessionId: string; date: string; status: SessionStatus; reason: string | null }

export interface TodayAttendance {
    date: string;
    state: 'CHECKED_IN' | 'CHECKED_OUT' | 'NOT_CHECKED_IN';
    activeSession: AttendanceSession | null;
    sessions: AttendanceSession[];
    summary: DaySummary | null;
    day: { type: DaySummary['dayType']; holidayName: string | null; leaveType: string | null; shiftName: string | null;
        expectedMinutes: number; expectedStart: string | null; expectedEnd: string | null };
    needsAttention: AttentionItem[];
    pendingAdjustments: number;
    rules: { checkInEnabled: boolean; checkOutEnabled: boolean; outsideLocationAction: 'ALLOW' | 'FLAG' | 'BLOCK'; maxSessionMinutes: number };
}

export interface HistoryDay { date: string; summary: DaySummary | null; sessions: AttendanceSession[] }

export interface Adjustment {
    id: string;
    employeeId: string;
    employeeName: string | null;
    employeeCode: string | null;
    date: string;
    type: AdjustmentType;
    status: 'pending' | 'approved' | 'rejected' | 'cancelled';
    source: 'EMPLOYEE' | 'ADMIN';
    sessionId: string | null;
    requestedCheckIn: string | null;
    requestedCheckOut: string | null;
    requestedLocationName: string | null;
    reason: string;
    reviewerName: string | null;
    reviewerComment: string | null;
    reviewedAt: string | null;
    createdAt: string;
    isMine: boolean;
    original: {
        session: { number: number; status: string; checkIn: string | null; checkOut: string | null; location: string | null; creditedMinutes: number | null } | null;
        day: { status: string; workedMinutes: number } | null;
    } | null;
}

export interface AdjustmentInput {
    adjustmentType: AdjustmentType;
    requestDate: string;
    sessionId?: string | null;
    requestedCheckIn?: string | null;
    requestedCheckOut?: string | null;
    locationId?: string | null;
    reason: string;
    employeeId?: string;
    reportingManagerId?: string | null;
}

export interface AdminSummary {
    date: string | null;
    totalEmployees: number; present: number; checkedInNow: number; absent: number; notCheckedIn: number; late: number;
    halfDay: number; onLeave: number; holiday: number; weekOff: number; wfh: number; missingCheckout: number;
    locationExceptions: number; pendingAdjustments: number;
}

export interface AdminDayRow {
    employeeId: string;
    employeeCode: string | null;
    employeeName: string | null;
    department: string | null;
    designation: string | null;
    date: string;
    status: DayStatus;
    firstCheckIn: string | null;
    lastCheckOut: string | null;
    workedMinutes: number;
    sessions: number;
    workMode: string | null;
    locations: string | null;
    isLate: boolean;
    lateMinutes: number;
    isEarlyCheckout: boolean;
    hasMissingCheckout: boolean;
    hasLocationException: boolean;
    checkedInNow: boolean;
    pendingAdjustments: number;
}

export interface AttendanceEvent {
    id: string;
    type: 'CHECK_IN' | 'CHECK_OUT';
    occurredAt: string;
    recordedAt: string;
    source: string;
    locationName: string | null;
    locationStatus: LocationStatus;
    workMode: string;
    latitude: number | null;
    longitude: number | null;
    accuracy: number | null;
    distance: number | null;
    adjustmentId: string | null;
    note: string | null;
}

export interface AuditEntry { id: string; action: string; createdAt: string; actorName: string | null; changes: any; metadata: any }

export interface DayDetail {
    employee: { id: string; code: string | null; name: string; department: string | null; designation: string | null };
    date: string;
    summary: DaySummary | null;
    sessions: AttendanceSession[];
    events: AttendanceEvent[];
    adjustments: Adjustment[];
    audit: AuditEntry[];
}

export interface AttendancePolicy {
    id: string | null;
    officeId: string | null;
    officeName?: string | null;
    timezone: string;
    attendanceEnabled: boolean;
    checkInEnabled: boolean;
    checkOutEnabled: boolean;
    allowMultipleSessions: boolean;
    expectedWorkMinutes: number;
    halfDayMinMinutes: number;
    defaultStartTime: string | null;
    lateGraceMinutes: number;
    earlyCheckoutGraceMinutes: number;
    weekOffDays: number[];
    locationValidationEnabled: boolean;
    outsideLocationAction: 'ALLOW' | 'FLAG' | 'BLOCK';
    maxGpsAccuracyM: number;
    boundaryToleranceCapM: number;
    allowWfh: boolean;
    maxSessionMinutes: number;
    requireAdjustmentApproval: boolean;
}

export interface Office {
    id: string;
    name: string;
    code: string | null;
    locationType: string;
    address: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    postalCode: string | null;
    timezone: string;
    latitude: number;
    longitude: number;
    /** Metres. */
    radius: number;
    status: 'Active' | 'Inactive';
}

export type OfficeInput = Partial<Omit<Office, 'id'>> & { name: string; latitude: number; longitude: number; radius: number };

export interface EmployeeLocation {
    id: string;
    kind: 'PRIMARY' | 'ALLOWED' | 'WFH' | 'CLIENT_SITE';
    officeId: string | null;
    officeName: string | null;
    label: string | null;
    latitude: number | null;
    longitude: number | null;
    radius: number | null;
    validFrom: string;
    validTo: string | null;
    isActive: boolean;
    approvedByName: string | null;
}

export interface ReportDay { date: string; hours: number; status: DayStatus | null; isLate: boolean; hasMissingCheckout: boolean; officeHours: number; wfhHours: number }

export interface ReportTotals {
    workedMinutes: number; presentDays: number; halfDays: number; absentDays: number; leaveDays: number;
    holidayDays: number; weekOffDays: number; lateDays: number; wfhDays: number; missingCheckoutDays: number;
    adjustments: number; avgMinutesPerDay: number; avgMinutesPerWeek: number;
}

export interface EmployeeReportDay {
    date: string;
    status: DayStatus;
    dayType: DaySummary['dayType'];
    workMode: string | null;
    firstCheckIn: string | null;
    lastCheckOut: string | null;
    workedMinutes: number;
    expectedMinutes: number | null;
    officeHours: number;
    wfhHours: number;
    sessions: number;
    isLate: boolean;
    lateMinutes: number;
    isEarlyCheckout: boolean;
    earlyMinutes: number;
    hasMissingCheckout: boolean;
    hasLocationException: boolean;
    adjustmentCount: number;
    locations: string | null;
}

export interface EmployeeReport {
    employee: { id: string; code: string | null; name: string; department: string | null; designation: string | null };
    from: string;
    to: string;
    days: EmployeeReportDay[];
    totals: ReportTotals;
    adjustments: Adjustment[];
}

export interface WorkforceReportRow {
    employeeId: string;
    employeeCode: string | null;
    employeeName: string | null;
    department: string | null;
    designation: string | null;
    presentDays: number;
    halfDays: number;
    absentDays: number;
    leaveDays: number;
    lateDays: number;
    wfhDays: number;
    missingCheckoutDays: number;
    workedMinutes: number;
    adjustments: number;
    avgMinutesPerDay: number;
    avgMinutesPerWeek: number;
}

export interface WorkforceReport { from: string; to: string; employees: WorkforceReportRow[] }

// ───────────────────────────── mappers ─────────────────────────────

const num = (v: any) => (v === null || v === undefined || v === '' ? null : Number(v));
/** Calendar date as YYYY-MM-DD. A timestamp is read in local time, never sliced (that would shift days across time zones). */
const day = (v: any): string => {
    if (!v) return '';
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const d = new Date(v);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** "1" for an original session; "1(a)", "1(b)", … for its approved adjustments. */
export function sessionLabel(number: number | null | undefined, revision: number | null | undefined): string {
    const base = String(number ?? '');
    const rev = Number(revision) || 0;
    return rev > 0 ? `${base}(${String.fromCharCode(96 + rev)})` : base;
}

export function mapSession(s: any): AttendanceSession {
    const revision = Number(s.revision) || 0;
    return {
        id: s.id,
        date: s.date_str || day(s.date),
        number: s.session_number,
        revision,
        label: sessionLabel(s.session_number, revision),
        status: s.status,
        checkIn: s.checkin_activity_time,
        checkOut: s.checkout_activity_time ?? null,
        checkInLocation: s.checkin_office_name ?? null,
        checkOutLocation: s.checkout_office_name ?? null,
        checkInLocationStatus: s.checkin_location_status ?? null,
        checkOutLocationStatus: s.checkout_location_status ?? null,
        checkInDistance: num(s.checkin_distance),
        checkInAccuracy: num(s.checkin_accuracy),
        workMode: s.work_mode ?? null,
        creditedMinutes: num(s.credited_minutes),
        needsReview: Boolean(s.needs_review),
        // A superseded session has been replaced by an approved correction, so its
        // original notes (e.g. "location was not available") no longer apply — show
        // one clear status instead of stacking the old message with the supersede note.
        exception: s.status === 'SUPERSEDED'
            ? 'Replaced by an approved correction'
            : [...new Set([s.checkin_exception, s.checkout_exception].filter(Boolean))].join(' ') || null,
        adjustmentId: s.adjustment_id ?? null,
    };
}

export function mapSummary(d: any): DaySummary | null {
    if (!d) return null;
    return {
        date: d.date_str || day(d.date),
        status: d.attendance_status,
        dayType: d.day_type || 'WORKING',
        workMode: d.work_mode ?? null,
        firstCheckIn: d.first_checkin ?? null,
        lastCheckOut: d.last_checkout ?? null,
        workedMinutes: Number(d.worked_minutes) || 0,
        expectedMinutes: num(d.expected_minutes),
        sessions: Number(d.total_sessions) || 0,
        isLate: Boolean(d.is_late),
        lateMinutes: Number(d.late_minutes) || 0,
        isEarlyCheckout: Boolean(d.is_early_checkout),
        earlyMinutes: Number(d.early_checkout_minutes) || 0,
        hasMissingCheckout: Boolean(d.has_missing_checkout),
        hasLocationException: Boolean(d.has_location_exception),
        adjustmentPending: Boolean(d.adjustment_pending),
        adjustmentCount: Number(d.adjustment_count) || 0,
    };
}

function mapAdjustment(r: any): Adjustment {
    const o = r.original_values || null;
    return {
        id: r.id,
        employeeId: r.employee_id,
        employeeName: r.employee_name ?? null,
        employeeCode: r.employee_code ?? null,
        date: r.request_date_str || day(r.request_date),
        type: r.adjustment_type,
        status: r.status,
        source: r.source || 'EMPLOYEE',
        sessionId: r.target_session_id ?? null,
        requestedCheckIn: r.requested_check_in ?? null,
        requestedCheckOut: r.requested_check_out ?? null,
        requestedLocationName: r.requested_location_name ?? null,
        reason: r.reason,
        reviewerName: r.reviewer_name ?? null,
        reviewerComment: r.admin_note ?? null,
        reviewedAt: r.reviewed_at ?? null,
        createdAt: r.created_at,
        isMine: Boolean(r.is_mine),
        original: o ? { session: o.session ?? null, day: o.day ?? null } : null,
    };
}

function mapReportTotals(t: any): ReportTotals {
    return {
        workedMinutes: Number(t?.workedMinutes) || 0,
        presentDays: Number(t?.presentDays) || 0,
        halfDays: Number(t?.halfDays) || 0,
        absentDays: Number(t?.absentDays) || 0,
        leaveDays: Number(t?.leaveDays) || 0,
        holidayDays: Number(t?.holidayDays) || 0,
        weekOffDays: Number(t?.weekOffDays) || 0,
        lateDays: Number(t?.lateDays) || 0,
        wfhDays: Number(t?.wfhDays) || 0,
        missingCheckoutDays: Number(t?.missingCheckoutDays) || 0,
        adjustments: Number(t?.adjustments) || 0,
        avgMinutesPerDay: Number(t?.avgMinutesPerDay) || 0,
        avgMinutesPerWeek: Number(t?.avgMinutesPerWeek) || 0,
    };
}

function mapEmployeeReportDay(r: any): EmployeeReportDay {
    return {
        date: r.date,
        status: r.attendance_status,
        dayType: r.day_type || 'WORKING',
        workMode: r.work_mode ?? null,
        firstCheckIn: r.first_checkin ?? null,
        lastCheckOut: r.last_checkout ?? null,
        workedMinutes: Number(r.worked_minutes) || 0,
        expectedMinutes: num(r.expected_minutes),
        officeHours: Number(r.office_hours) || 0,
        wfhHours: Number(r.wfh_hours) || 0,
        sessions: Number(r.sessions) || 0,
        isLate: Boolean(r.is_late),
        lateMinutes: Number(r.late_minutes) || 0,
        isEarlyCheckout: Boolean(r.is_early_checkout),
        earlyMinutes: Number(r.early_minutes) || 0,
        hasMissingCheckout: Boolean(r.has_missing_checkout),
        hasLocationException: Boolean(r.has_location_exception),
        adjustmentCount: Number(r.adjustment_count) || 0,
        locations: r.locations ?? null,
    };
}

function mapEmployeeReport(d: any): EmployeeReport {
    return {
        employee: {
            id: d.employee.id, code: d.employee.employee_code ?? null,
            name: [d.employee.first_name, d.employee.last_name].filter(Boolean).join(' ') || 'Employee',
            department: d.employee.department ?? null, designation: d.employee.designation ?? null,
        },
        from: d.from,
        to: d.to,
        days: (d.days || []).map(mapEmployeeReportDay),
        totals: mapReportTotals(d.totals),
        adjustments: (d.adjustments || []).map(mapAdjustment),
    };
}

function mapWorkforceRow(r: any): WorkforceReportRow {
    return {
        employeeId: r.employee_id,
        employeeCode: r.employee_code ?? null,
        employeeName: r.employee_name ?? null,
        department: r.department ?? null,
        designation: r.designation ?? null,
        presentDays: Number(r.present_days) || 0,
        halfDays: Number(r.half_days) || 0,
        absentDays: Number(r.absent_days) || 0,
        leaveDays: Number(r.leave_days) || 0,
        lateDays: Number(r.late_days) || 0,
        wfhDays: Number(r.wfh_days) || 0,
        missingCheckoutDays: Number(r.missing_checkout_days) || 0,
        workedMinutes: Number(r.worked_minutes) || 0,
        adjustments: Number(r.adjustments) || 0,
        avgMinutesPerDay: Number(r.avg_minutes_per_day) || 0,
        avgMinutesPerWeek: Number(r.avg_minutes_per_week) || 0,
    };
}

function mapToday(t: any): TodayAttendance {
    return {
        date: t.date,
        state: t.state,
        activeSession: t.activeSession ? mapSession(t.activeSession) : null,
        sessions: (t.sessions || []).map(mapSession),
        summary: mapSummary(t.summary),
        day: t.day,
        needsAttention: (t.needsAttention || []).map((a: any) => ({ sessionId: a.session_id, date: a.date, status: a.status, reason: a.reason })),
        pendingAdjustments: t.pendingAdjustments || 0,
        rules: t.rules,
    };
}

function mapOffice(o: any): Office {
    return {
        id: o.id, name: o.name, code: o.code ?? null, locationType: o.location_type || 'OFFICE', address: o.address ?? null,
        city: o.city ?? null, state: o.state ?? null, country: o.country ?? null, postalCode: o.postal_code ?? null,
        timezone: o.timezone || 'Asia/Kolkata', latitude: Number(o.latitude), longitude: Number(o.longitude),
        radius: Number(o.radius_in_meters ?? o.radius ?? 100), status: o.status || 'Active',
    };
}

const officePayload = (o: Partial<OfficeInput>) => ({
    name: o.name, code: o.code, locationType: o.locationType, address: o.address, city: o.city, state: o.state,
    country: o.country, postalCode: o.postalCode, timezone: o.timezone, latitude: o.latitude, longitude: o.longitude,
    radiusInMeters: o.radius, status: o.status,
});

// ───────────────────────────── helpers ─────────────────────────────

const dataOf = (res: any) => res.data?.data ?? res.data;

/** Error with the server's human message and machine code attached. */
export class AttendanceError extends Error {
    constructor(message: string, public code?: string, public details?: any) { super(message); }
}
const fail = (e: any, fallback: string) =>
    new AttendanceError(e?.response?.data?.message || e?.message || fallback, e?.response?.data?.code, e?.response?.data?.details);

/** Random key identifying one check-in/out attempt (see Idempotency-Key in the People API). */
function newIdempotencyKey() {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `att-${Date.now()}-${secureRandom().toString(36).slice(2, 12)}`;
}

/**
 * Full refresh after a check-in/out, without holding up the button. Runs twice:
 * straight away, and again shortly after for details filled in on the server
 * afterwards (the day summary, the detected Home place name).
 */
function refreshSoon(get: () => { fetchToday: () => Promise<void> }) {
    get().fetchToday().catch(() => undefined);
    setTimeout(() => get().fetchToday().catch(() => undefined), 4000);
}

export interface GpsPayload { latitude?: number; longitude?: number; accuracy?: number }

// ───────────────────────────── store ─────────────────────────────

interface AttendanceState {
    today: TodayAttendance | null;
    todayLoading: boolean;
    /** True while a check-in/out request is in flight (separate from loading). */
    acting: boolean;

    history: HistoryDay[];
    historyLoading: boolean;
    adjustments: Adjustment[];
    adjustmentsLoading: boolean;
    reports: ReportDay[];

    adminSummary: AdminSummary | null;
    adminRows: AdminDayRow[];
    adminLoading: boolean;

    policies: AttendancePolicy[];
    offices: Office[];
    /** Active locations anyone may pick in an adjustment request. */
    pickableLocations: { id: string; name: string; city: string | null }[];

    fetchToday: () => Promise<void>;
    /** `data` may be a promise (a GPS reading still arriving): the screen updates first, then it is awaited. */
    checkIn: (data: GpsPayload | Promise<GpsPayload>) => Promise<AttendanceSession>;
    checkOut: (data: GpsPayload | Promise<GpsPayload>) => Promise<AttendanceSession>;
    fetchHistory: (from?: string, to?: string, employeeId?: string) => Promise<HistoryDay[]>;
    fetchReports: (range: 'week' | 'month') => Promise<void>;
    /** One employee's period report (own data by default; a visible employee when employeeId is given). */
    fetchEmployeeReport: (from: string, to: string, employeeId?: string) => Promise<EmployeeReport>;
    /** Aggregated period report over every visible employee (managers & HR). */
    fetchWorkforceReport: (from: string, to: string, search?: string) => Promise<WorkforceReport>;

    fetchAdjustments: (q?: { status?: string; mine?: boolean }) => Promise<void>;
    requestAdjustment: (input: AdjustmentInput) => Promise<Adjustment>;
    cancelAdjustment: (id: string) => Promise<void>;
    reviewAdjustment: (id: string, decision: 'approved' | 'rejected', note?: string) => Promise<void>;
    adminCorrection: (input: AdjustmentInput & { employeeId: string }) => Promise<void>;

    fetchAdminSummary: (date?: string) => Promise<void>;
    fetchAdminDaily: (q?: { date?: string; status?: string; search?: string }) => Promise<void>;
    fetchDayDetail: (date: string, employeeId?: string) => Promise<DayDetail>;

    fetchPolicies: () => Promise<void>;
    updatePolicy: (patch: Partial<AttendancePolicy>, officeId?: string | null) => Promise<AttendancePolicy>;
    fetchOffices: () => Promise<void>;
    fetchPickableLocations: () => Promise<void>;
    saveOffice: (input: OfficeInput, id?: string) => Promise<Office>;
    deleteOffice: (id: string) => Promise<void>;
    fetchEmployeeLocations: (employeeId: string) => Promise<EmployeeLocation[]>;
    addEmployeeLocation: (employeeId: string, input: Record<string, unknown>) => Promise<void>;
    removeEmployeeLocation: (id: string) => Promise<void>;
}

export const useAttendanceStore = create<AttendanceState>((set, get) => ({
    today: null,
    todayLoading: false,
    acting: false,
    history: [],
    historyLoading: false,
    adjustments: [],
    adjustmentsLoading: false,
    reports: [],
    adminSummary: null,
    adminRows: [],
    adminLoading: false,
    policies: [],
    offices: [],
    pickableLocations: [],

    fetchToday: async () => {
        set({ todayLoading: true });
        try {
            set({ today: mapToday(dataOf(await apiClient.get('/people/attendance/today'))), todayLoading: false });
        } catch (e) {
            set({ todayLoading: false });
            throw fail(e, 'Could not load your attendance');
        }
    },

    checkIn: async (data) => {
        // Show the result immediately; the server confirms in the background and
        // the screen is corrected (and the error shown) if it refuses.
        const before = get().today;
        const now = new Date().toISOString();
        if (before) {
            const nextNumber = before.sessions.length + 1;
            const pending: AttendanceSession = {
                id: 'pending', date: before.date, number: nextNumber, revision: 0, label: String(nextNumber),
                status: 'OPEN', checkIn: now, checkOut: null,
                checkInLocation: null, checkOutLocation: null, checkInLocationStatus: null, checkOutLocationStatus: null,
                checkInDistance: null, checkInAccuracy: null, workMode: null, creditedMinutes: null, needsReview: false,
                exception: null, adjustmentId: null,
            };
            set({ today: { ...before, state: 'CHECKED_IN', activeSession: pending, sessions: [...before.sessions, pending] } });
        }
        set({ acting: true });
        try {
            // One key per button press: a retried request is recognised server-side and not recorded twice.
            const res = await apiClient.post('/people/attendance/check-in', await data, { headers: { 'Idempotency-Key': newIdempotencyKey() } });
            const session = mapSession(dataOf(res));
            const cur = get().today;
            if (cur) set({ today: { ...cur, state: 'CHECKED_IN', activeSession: session, sessions: cur.sessions.map((x) => (x.id === 'pending' ? session : x)) } });
            refreshSoon(get);
            return session;
        } catch (e) {
            set({ today: before });
            get().fetchToday().catch(() => undefined);
            throw fail(e, 'Check-in failed');
        } finally {
            set({ acting: false });
        }
    },

    checkOut: async (data) => {
        const before = get().today;
        const now = new Date().toISOString();
        if (before?.activeSession) {
            const closed: AttendanceSession = {
                ...before.activeSession, status: 'COMPLETED', checkOut: now,
                creditedMinutes: Math.max(0, Math.round((Date.now() - new Date(before.activeSession.checkIn).getTime()) / 60000)),
            };
            set({ today: { ...before, state: 'CHECKED_OUT', activeSession: null, sessions: before.sessions.map((x) => (x.id === closed.id ? closed : x)) } });
        }
        set({ acting: true });
        try {
            const res = await apiClient.post('/people/attendance/check-out', await data, { headers: { 'Idempotency-Key': newIdempotencyKey() } });
            const session = mapSession(dataOf(res));
            const cur = get().today;
            if (cur) set({ today: { ...cur, state: 'CHECKED_OUT', activeSession: null, sessions: cur.sessions.map((x) => (x.id === session.id ? session : x)) } });
            refreshSoon(get);
            return session;
        } catch (e) {
            set({ today: before });
            get().fetchToday().catch(() => undefined);
            throw fail(e, 'Check-out failed');
        } finally {
            set({ acting: false });
        }
    },

    fetchHistory: async (from, to, employeeId) => {
        set({ historyLoading: true });
        try {
            const rows = (dataOf(await apiClient.get('/people/attendance/history', { params: { from, to, employeeId } })) || []).map((d: any) => ({
                date: d.date, summary: mapSummary(d.summary), sessions: (d.sessions || []).map(mapSession),
            }));
            if (!employeeId) set({ history: rows });
            return rows;
        } catch (e) {
            throw fail(e, 'Could not load attendance history');
        } finally {
            set({ historyLoading: false });
        }
    },

    fetchReports: async (range) => {
        try {
            const rows = dataOf(await apiClient.get('/people/attendance/reports', { params: { range } })) || [];
            set({
                reports: rows.map((r: any) => ({
                    date: r.date, hours: Number(r.total_hours) || 0, status: r.status ?? null, isLate: Boolean(r.is_late),
                    hasMissingCheckout: Boolean(r.has_missing_checkout),
                    officeHours: Number((r.details || []).find((d: any) => d.type === 'Office')?.hours) || 0,
                    wfhHours: Number((r.details || []).find((d: any) => d.type === 'WFH')?.hours) || 0,
                })),
            });
        } catch (e) {
            throw fail(e, 'Could not load attendance insights');
        }
    },

    fetchEmployeeReport: async (from, to, employeeId) => {
        try {
            return mapEmployeeReport(dataOf(await apiClient.get('/people/attendance/reports/employee', { params: { from, to, employeeId } })));
        } catch (e) {
            throw fail(e, 'Could not load the attendance report');
        }
    },

    fetchWorkforceReport: async (from, to, search) => {
        try {
            const d = dataOf(await apiClient.get('/people/attendance/reports/workforce', { params: { from, to, search: search || undefined } }));
            return { from: d.from, to: d.to, employees: (d.employees || []).map(mapWorkforceRow) };
        } catch (e) {
            throw fail(e, 'Could not load the workforce report');
        }
    },

    fetchAdjustments: async (q) => {
        set({ adjustmentsLoading: true });
        try {
            const rows = dataOf(await apiClient.get('/people/attendance/regularization', { params: { status: q?.status, mine: q?.mine ? 'true' : undefined } })) || [];
            set({ adjustments: rows.map(mapAdjustment) });
        } catch (e) {
            throw fail(e, 'Could not load adjustment requests');
        } finally {
            set({ adjustmentsLoading: false });
        }
    },

    requestAdjustment: async (input) => {
        try {
            const row = mapAdjustment(dataOf(await apiClient.post('/people/attendance/regularization', input)));
            get().fetchToday().catch(() => undefined);
            return row;
        } catch (e) {
            throw fail(e, 'Could not submit the request');
        }
    },

    cancelAdjustment: async (id) => {
        try {
            await apiClient.post(`/people/attendance/regularization/${id}/cancel`);
            set((s) => ({ adjustments: s.adjustments.map((a) => (a.id === id ? { ...a, status: 'cancelled' } : a)) }));
        } catch (e) {
            throw fail(e, 'Could not cancel the request');
        }
    },

    reviewAdjustment: async (id, decision, note) => {
        try {
            await apiClient.post(`/people/attendance/regularization/${id}/process`, { status: decision, note });
            set((s) => ({ adjustments: s.adjustments.map((a) => (a.id === id ? { ...a, status: decision, reviewerComment: note ?? a.reviewerComment } : a)) }));
        } catch (e) {
            throw fail(e, 'Could not save the decision');
        }
    },

    adminCorrection: async (input) => {
        try {
            await apiClient.post('/people/attendance/admin/corrections', input);
        } catch (e) {
            throw fail(e, 'Could not record the correction');
        }
    },

    fetchAdminSummary: async (date) => {
        try {
            set({ adminSummary: dataOf(await apiClient.get('/people/attendance/admin/summary', { params: { date } })) });
        } catch (e) {
            throw fail(e, 'Could not load the attendance dashboard');
        }
    },

    fetchAdminDaily: async (q) => {
        set({ adminLoading: true });
        try {
            const rows = dataOf(await apiClient.get('/people/attendance/admin/daily', { params: q })) || [];
            set({
                adminRows: rows.map((r: any) => ({
                    employeeId: r.employee_id, employeeCode: r.employee_code ?? null, employeeName: r.employee_name ?? null,
                    department: r.department ?? null, designation: r.designation ?? null, date: r.date, status: r.status,
                    firstCheckIn: r.first_checkin ?? null, lastCheckOut: r.last_checkout ?? null, workedMinutes: Number(r.worked_minutes) || 0,
                    sessions: Number(r.sessions) || 0, workMode: r.work_mode ?? null, locations: r.locations ?? null,
                    isLate: Boolean(r.is_late), lateMinutes: Number(r.late_minutes) || 0, isEarlyCheckout: Boolean(r.is_early_checkout),
                    hasMissingCheckout: Boolean(r.has_missing_checkout), hasLocationException: Boolean(r.has_location_exception),
                    checkedInNow: Boolean(r.checked_in_now), pendingAdjustments: Number(r.pending_adjustments) || 0,
                })),
            });
        } catch (e) {
            throw fail(e, 'Could not load team attendance');
        } finally {
            set({ adminLoading: false });
        }
    },

    fetchDayDetail: async (date, employeeId) => {
        try {
            const d = dataOf(await apiClient.get(`/people/attendance/day/${date}`, { params: { employeeId } }));
            return {
                employee: {
                    id: d.employee.id, code: d.employee.employee_code ?? null,
                    name: [d.employee.first_name, d.employee.last_name].filter(Boolean).join(' ') || 'Employee',
                    department: d.employee.department ?? null, designation: d.employee.designation ?? null,
                },
                date: d.date,
                summary: mapSummary(d.summary),
                sessions: (d.sessions || []).map(mapSession),
                events: (d.events || []).map((e: any) => ({
                    id: e.id, type: e.event_type, occurredAt: e.occurred_at, recordedAt: e.recorded_at, source: e.source,
                    locationName: e.location_name ?? null, locationStatus: e.location_status, workMode: e.work_mode,
                    latitude: num(e.latitude), longitude: num(e.longitude), accuracy: num(e.accuracy_m), distance: num(e.distance_m),
                    adjustmentId: e.adjustment_id ?? null, note: e.note ?? null,
                })),
                adjustments: (d.adjustments || []).map(mapAdjustment),
                audit: (d.audit || []).map((a: any) => ({ id: a.id, action: a.action, createdAt: a.created_at, actorName: a.actor_name ?? null, changes: a.changes, metadata: a.metadata })),
            };
        } catch (e) {
            throw fail(e, 'Could not load that day');
        }
    },

    fetchPolicies: async () => {
        try {
            set({ policies: dataOf(await apiClient.get('/people/attendance/settings')) || [] });
        } catch (e) {
            throw fail(e, 'Could not load attendance rules');
        }
    },

    updatePolicy: async (patch, officeId) => {
        try {
            const saved: AttendancePolicy = dataOf(await apiClient.put('/people/attendance/settings', patch, { params: { officeId: officeId || undefined } }));
            await get().fetchPolicies();
            return saved;
        } catch (e) {
            throw fail(e, 'Could not save the rules');
        }
    },

    fetchOffices: async () => {
        try {
            set({ offices: (dataOf(await apiClient.get('/people/offices')) || []).map(mapOffice) });
        } catch (e) {
            // Employee forms read this list opportunistically; don't break them.
            console.warn('Could not load offices', e);
        }
    },

    fetchPickableLocations: async () => {
        try {
            const rows = dataOf(await apiClient.get('/people/attendance/locations')) || [];
            set({ pickableLocations: rows.map((o: any) => ({ id: o.id, name: o.name, city: o.city ?? null })) });
        } catch (e) {
            console.warn('Could not load locations', e);
        }
    },

    saveOffice: async (input, id) => {
        try {
            const res = id
                ? await apiClient.patch(`/people/offices/${id}`, officePayload(input))
                : await apiClient.post('/people/offices', officePayload(input));
            const office = mapOffice(dataOf(res));
            set((s) => ({ offices: id ? s.offices.map((o) => (o.id === id ? office : o)) : [...s.offices, office].sort((a, b) => a.name.localeCompare(b.name)) }));
            return office;
        } catch (e) {
            throw fail(e, 'Could not save the location');
        }
    },

    deleteOffice: async (id) => {
        try {
            await apiClient.delete(`/people/offices/${id}`);
            set((s) => ({ offices: s.offices.filter((o) => o.id !== id) }));
        } catch (e) {
            throw fail(e, 'Could not delete the location');
        }
    },

    fetchEmployeeLocations: async (employeeId) => {
        try {
            const rows = dataOf(await apiClient.get(`/people/attendance/employees/${employeeId}/locations`)) || [];
            return rows.map((l: any) => ({
                id: l.id, kind: l.kind, officeId: l.office_id ?? null, officeName: l.office_name ?? null, label: l.label ?? null,
                latitude: num(l.latitude), longitude: num(l.longitude), radius: num(l.radius_m), validFrom: day(l.valid_from),
                validTo: l.valid_to ? day(l.valid_to) : null, isActive: Boolean(l.is_active), approvedByName: l.approved_by_name ?? null,
            }));
        } catch (e) {
            throw fail(e, 'Could not load assigned locations');
        }
    },

    addEmployeeLocation: async (employeeId, input) => {
        try {
            await apiClient.post(`/people/attendance/employees/${employeeId}/locations`, input);
        } catch (e) {
            throw fail(e, 'Could not assign the location');
        }
    },

    removeEmployeeLocation: async (id) => {
        try {
            await apiClient.delete(`/people/attendance/employee-locations/${id}`);
        } catch (e) {
            throw fail(e, 'Could not remove the location');
        }
    },
}));
